import { FC, useCallback, useMemo, useState } from "react"
import { Box, Button, Chip, CircularProgress, Tooltip } from "@mui/material"
import {
  Add,
  AddToQueue,
  DeleteOutlined,
  OpenInNew,
  PlayArrow,
  Block,
  Sync,
} from "@mui/icons-material"
import { GridColDef, GridRowParams } from "@mui/x-data-grid-pro"
import { useNavigate } from "react-router-dom"
import { DataGrid, Modal, SnackbarAlert } from "@/core/components"
import { useUrlPagination } from "@/core/hooks"
import { GuestBot } from "../../interfaces"
import {
  useBotsListQuery,
  useProvisionListQuery,
  usePatchBotMutation,
  useDeleteBotMutation,
  useRefreshProfileMutation,
} from "../../store"
import { useGuestBotWriteAccess } from "../../hooks"
import { isProvisionActive } from "../../utils/provisionStatus"
import { CreateGuestBotForm } from "../forms"
import { TooltipAction } from "../common"

interface IProps {
  onCreateViaBotFather?: () => void
}

export const BotsTable: FC<IProps> = ({ onCreateViaBotFather }) => {
  const { page, pageSize, paginationModel, setPaginationModel } =
    useUrlPagination(50)
  const { data, isFetching } = useBotsListQuery({
    queryParams: { limit: pageSize, offset: page * pageSize },
  })
  const { data: provisionData } = useProvisionListQuery()
  const activeProvisions = useMemo(
    () =>
      (provisionData?.items ?? []).filter((p) =>
        isProvisionActive(p.status, p.lastError),
      ),
    [provisionData],
  )
  const rows = data?.items ?? []
  const canWrite = useGuestBotWriteAccess()
  const navigate = useNavigate()
  const [patchBot] = usePatchBotMutation()
  const [deleteBot] = useDeleteBotMutation()
  const [refreshProfile, { isLoading: isRefreshing }] =
    useRefreshProfileMutation()

  const [creating, setCreating] = useState(false)
  const [toDelete, setToDelete] = useState<GuestBot.Bot | null>(null)
  const [snackbar, setSnackbar] = useState<{
    variant: "success" | "error"
    message: string
  } | null>(null)

  const handleRefresh = useCallback(
    async (bot: GuestBot.Bot) => {
      try {
        await refreshProfile({ pathParams: { id: bot.id } }).unwrap()
        setSnackbar({
          variant: "success",
          message: "Данные бота синхронизированы из Telegram",
        })
      } catch {
        setSnackbar({
          variant: "error",
          message: "Не удалось синхронизировать данные бота",
        })
      }
    },
    [refreshProfile],
  )

  const columns: GridColDef<GuestBot.Bot>[] = useMemo(
    () => [
      {
        field: "username",
        headerName: "Бот",
        flex: 3,
        valueGetter: (params) =>
          params.row.username ?? String(params.row.botId),
        renderCell: (params) => {
          const row = params.row
          const creating = activeProvisions.some(
            (p) =>
              (row.botId != null && p.botId === row.botId) ||
              (p.usernameSnapshot != null &&
                p.usernameSnapshot === row.username),
          )
          return (
            <Box display="flex" alignItems="center" gap={1}>
              <Box component="span">{row.username ?? String(row.botId)}</Box>
              {creating && (
                <Chip
                  size="small"
                  color="info"
                  icon={<CircularProgress size={14} color="inherit" />}
                  label="Создаётся…"
                />
              )}
            </Box>
          )
        },
      },
      {
        field: "name",
        headerName: "Имя",
        flex: 3,
        valueGetter: (params) => params.row.name ?? "",
      },
      {
        field: "shortDescription",
        headerName: "О боте",
        flex: 3,
        valueGetter: (params) => params.row.shortDescription ?? "",
      },
      {
        field: "description",
        headerName: "Описание",
        flex: 3,
        valueGetter: (params) => params.row.description ?? "",
      },
      {
        field: "status",
        headerName: "Статус",
        flex: 1,
        renderCell: (params) => (
          <Chip
            size="small"
            label={params.row.status === "working" ? "Включён" : "Выключен"}
            color={params.row.status === "working" ? "success" : "error"}
          />
        ),
      },
      { field: "delaySeconds", headerName: "Задержка, сек", flex: 1 },
      { field: "lastError", headerName: "Последняя ошибка", flex: 3 },
    ],
    [activeProvisions],
  )

  const rowActions = useMemo(() => {
    const actions: ((
      params: GridRowParams<GuestBot.Bot>,
    ) => React.ReactElement)[] = [
      (params) => (
        <TooltipAction
          icon={<OpenInNew />}
          label="Открыть"
          onClick={() => navigate(`/telegram/guest-bots/${params.row.id}`)}
        />
      ),
    ]
    if (canWrite) {
      actions.push(
        (params) => (
          <TooltipAction
            icon={<Sync />}
            label="Синхронизировать из Telegram"
            disabled={isRefreshing}
            onClick={() => handleRefresh(params.row)}
          />
        ),
        (params) =>
          params.row.status === "working" ? (
            <TooltipAction
              icon={<Block />}
              label="Выключить"
              onClick={() =>
                patchBot({
                  pathParams: { id: params.row.id },
                  body: { status: "banned" },
                })
              }
            />
          ) : (
            <TooltipAction
              icon={<PlayArrow />}
              label="Включить"
              onClick={() =>
                patchBot({
                  pathParams: { id: params.row.id },
                  body: { status: "working" },
                })
              }
            />
          ),
        (params) => (
          <TooltipAction
            icon={<DeleteOutlined />}
            label="Удалить"
            onClick={() => setToDelete(params.row)}
          />
        ),
      )
    }
    return actions
  }, [canWrite, navigate, patchBot, isRefreshing, handleRefresh])

  return (
    <>
      {canWrite && (
        <Box mb={1} display="flex" justifyContent="flex-end" gap={1}>
          {onCreateViaBotFather && (
            <Button startIcon={<AddToQueue />} onClick={onCreateViaBotFather}>
              Создать через BotFather
            </Button>
          )}
          <Tooltip title="Добавить нового гостевого бота" arrow>
            <Button startIcon={<Add />} onClick={() => setCreating(true)}>
              Добавить бота
            </Button>
          </Tooltip>
        </Box>
      )}
      <Box height="70vh">
        <DataGrid<GuestBot.Bot>
          columns={columns}
          rows={rows}
          getRowId={(row) => row.id}
          loading={isFetching}
          paginationMode="server"
          rowCount={data?.total ?? 0}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          pagination
          rowActions={rowActions}
          actionsColumnFlex={2}
        />
      </Box>

      {creating && (
        <Modal
          disableBackdropClose
          title="Новый гостевой бот"
          onClose={() => setCreating(false)}
          element={<CreateGuestBotForm onDone={() => setCreating(false)} />}
        />
      )}
      {toDelete && (
        <Modal
          disableBackdropClose
          title="Удалить бота?"
          onClose={() => setToDelete(null)}
          element={
            <Box>
              {toDelete.username ?? toDelete.botId}
              <Box mt={2} display="flex" justifyContent="flex-end" gap={1}>
                <Tooltip title="Отменить удаление" arrow>
                  <Button onClick={() => setToDelete(null)}>Отмена</Button>
                </Tooltip>
                <Tooltip
                  title="Удалить бота без возможности восстановления"
                  arrow
                >
                  <Button
                    color="error"
                    variant="contained"
                    onClick={async () => {
                      await deleteBot({ pathParams: { id: toDelete.id } })
                      setToDelete(null)
                    }}
                  >
                    Удалить
                  </Button>
                </Tooltip>
              </Box>
            </Box>
          }
        />
      )}
      <SnackbarAlert
        isOpen={!!snackbar}
        variant={snackbar?.variant}
        message={snackbar?.message ?? ""}
        onClose={() => setSnackbar(null)}
      />
    </>
  )
}
